import { useState, useEffect, useMemo, useRef } from 'react';
import { loadMarketProbability } from './marketPricesClient';
import type { OHLCVPoint } from './types';

const EMPTY_MAP: ReadonlyMap<string, OHLCVPoint[]> = new Map();

/** Identity of a request, so held data can be told apart from what is wanted. */
export function outcomeSeriesKey(
    conditionId: string,
    startDate: Date,
    endDate: Date,
    intervalMinutes: number,
    outcomeIndex: number
): string {
    return [
        conditionId,
        startDate.getTime(),
        endDate.getTime(),
        intervalMinutes,
        outcomeIndex,
    ].join('|');
}

/**
 * One outcome side of several markets, for the window currently being drawn.
 *
 * Only the NO side needs this — the YES side arrives through
 * useMultiMarketProbability — so it stays narrow: no lead-market logic, no
 * loading flags, just the points.
 *
 * **Staleness is handled here, not by callers.** A fetch in flight leaves the
 * previous window's points in hand, and pairing those with a freshly-windowed
 * YES series stacks a stale series under a current one — re-bucketed at a new
 * resolution, a day of volume becomes a single bar. So each market's points are
 * stored beside the request that produced them, and a market is absent from the
 * returned map until its data describes the window being asked for. Callers
 * cannot get this wrong, because they are never handed anything stale.
 *
 * Markets resolve independently, so a slow one does not hold back the rest.
 */
export function useMarketsOutcomeSeries(
    conditionIds: string[],
    startDate: Date | null,
    endDate: Date | null,
    intervalMinutes: number,
    outcomeIndex: number
): ReadonlyMap<string, OHLCVPoint[]> {
    const [held, setHeld] = useState<
        Map<string, { key: string; points: OHLCVPoint[] }>
    >(() => new Map());
    const idsKey = conditionIds.join(',');
    const heldRef = useRef(held);
    heldRef.current = held;

    useEffect(() => {
        if (conditionIds.length === 0 || !startDate || !endDate) return;
        const controller = new AbortController();

        for (const conditionId of conditionIds) {
            const requestKey = outcomeSeriesKey(
                conditionId,
                startDate,
                endDate,
                intervalMinutes,
                outcomeIndex
            );
            // Already holding exactly this request's answer — don't refetch.
            if (heldRef.current.get(conditionId)?.key === requestKey) continue;

            // try/catch rather than a .catch tail, so the rejection is
            // handled in the same turn it is created.
            void (async () => {
                let points: OHLCVPoint[] = [];
                try {
                    points = await loadMarketProbability(
                        conditionId,
                        startDate,
                        endDate,
                        intervalMinutes,
                        { signal: controller.signal },
                        outcomeIndex
                    );
                } catch (err) {
                    if (
                        controller.signal.aborted ||
                        (err as { name?: string })?.name === 'AbortError'
                    )
                        return;
                    console.error(
                        `outcome series failed for ${conditionId}/${outcomeIndex}:`,
                        err
                    );
                    // An absent side is not fatal — the volume panel falls back
                    // to the YES side alone and candles to mirroring. Recorded
                    // against the request so it is not retried in a loop.
                }
                if (controller.signal.aborted) return;
                setHeld(prev => {
                    const next = new Map(prev);
                    next.set(conditionId, { key: requestKey, points });
                    return next;
                });
            })();
        }

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        idsKey,
        startDate?.getTime(),
        endDate?.getTime(),
        intervalMinutes,
        outcomeIndex,
    ]);

    // Only what describes the window being drawn right now.
    return useMemo(() => {
        if (conditionIds.length === 0 || !startDate || !endDate)
            return EMPTY_MAP;
        const current = new Map<string, OHLCVPoint[]>();
        for (const conditionId of conditionIds) {
            const entry = held.get(conditionId);
            if (!entry) continue;
            const wanted = outcomeSeriesKey(
                conditionId,
                startDate,
                endDate,
                intervalMinutes,
                outcomeIndex
            );
            if (entry.key === wanted && entry.points.length > 0)
                current.set(conditionId, entry.points);
        }
        return current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        held,
        idsKey,
        startDate?.getTime(),
        endDate?.getTime(),
        intervalMinutes,
        outcomeIndex,
    ]);
}
